import React, { useState } from 'react';
import { Mail, Phone, MapPin, Send, FileText } from 'lucide-react';
import { contactInfo, heroImages } from '../data/mock';
import { toast } from 'sonner';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    serviceInterest: '',
    message: '',
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Mock submission - in real implementation, this would send to backend
    console.log('Contact form submitted:', formData);
    toast.success('Thank you! We\'ll get back to you within 24 hours.');
    
    // Reset form
    setFormData({
      name: '',
      email: '',
      phone: '',
      serviceInterest: '',
      message: '',
    });
  };

  return (
    <div style={{ paddingTop: '80px' }}>
      {/* Hero Section */}
      <section style={{ 
        background: 'var(--gradient-hero-subtle)', 
        padding: '5rem 1.5rem 3rem',
        textAlign: 'center'
      }}>
        <div className="container" style={{ maxWidth: '900px' }}>
          <h1 className="heading-hero" style={{ marginBottom: '1.5rem' }}>
            Let's Connect!
          </h1>
          <p className="body-large" style={{ color: 'var(--text-secondary)', marginBottom: '1rem' }}>
            Ready to build your business with clarity and inner peace?
          </p>
          <p className="body-medium" style={{ color: 'var(--text-primary)', fontStyle: 'italic' }}>
            {contactInfo.tagline}
          </p>
        </div>
      </section>

      {/* Contact Form & Info */}
      <section className="section" style={{ background: 'var(--bg-page)' }}>
        <div className="container !mx-[10px]">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '3rem' }}>
            {/* Contact Information */}
            <div>
              <h2 className="heading-2" style={{ marginBottom: '1.5rem' }}>
                Get In Touch
              </h2>
              <p className="body-medium" style={{ marginBottom: '2rem', color: 'var(--text-secondary)' }}>
                Whether you're just starting out or ready to scale, we're here to help. Reach out and let's discuss how we can support your journey.
              </p>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem', marginBottom: '2rem' }}>
                <div className="card" style={{ padding: '1.5rem', display: 'flex', gap: '1rem', alignItems: 'flex-start' }}>
                  <div style={{ 
                    width: '3rem', 
                    height: '3rem', 
                    background: 'var(--accent-purple-200)',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0
                  }}>
                    <Mail size={20} color="var(--accent-purple-400)" />
                  </div>
                  <div>
                    <h3 className="heading-3" style={{ marginBottom: '0.5rem' }}>
                      Email
                    </h3>
                    <a 
                      href={`mailto:${contactInfo.email}`}
                      className="body-medium"
                      style={{ color: 'var(--text-secondary)', textDecoration: 'none' }}
                    >
                      {contactInfo.email}
                    </a>
                  </div>
                </div>

                <div className="card" style={{ padding: '1.5rem', display: 'flex', gap: '1rem', alignItems: 'flex-start' }}>
                  <div style={{ 
                    width: '3rem', 
                    height: '3rem', 
                    background: 'var(--accent-blue-200)',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0
                  }}>
                    <Phone size={20} color="var(--accent-blue-400)" />
                  </div>
                  <div>
                    <h3 className="heading-3" style={{ marginBottom: '0.5rem' }}>
                      Phone
                    </h3>
                    <a 
                      href={`tel:${contactInfo.phone.replace(/\D/g, '')}`}
                      className="body-medium"
                      style={{ color: 'var(--text-secondary)', textDecoration: 'none' }}
                    >
                      {contactInfo.phone}
                    </a>
                  </div>
                </div>

                <div className="card" style={{ padding: '1.5rem', display: 'flex', gap: '1rem', alignItems: 'flex-start' }}>
                  <div style={{ 
                    width: '3rem', 
                    height: '3rem', 
                    background: 'var(--accent-orange-200)',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0
                  }}>
                    <FileText size={20} color="var(--accent-orange-400)" />
                  </div>
                  <div>
                    <h3 className="heading-3" style={{ marginBottom: '0.5rem' }}>
                      Vision Alignment Call
                    </h3>
                    <a 
                      href="https://forms.gle/T1XryTU1b4NUd3gS7"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="body-medium"
                      style={{ color: 'var(--text-secondary)', textDecoration: 'none' }}
                    >
                      Fill out intake form
                    </a>
                  </div>
                </div>
              </div>

              <div style={{ 
                background: 'var(--accent-pink-200)',
                padding: '1.5rem',
                borderRadius: '0.75rem'
              }}>
                <h3 className="heading-3" style={{ marginBottom: '0.75rem' }}>
                  What to Expect
                </h3>
                <ul style={{ margin: 0, paddingLeft: '1.5rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  <li className="body-small" style={{ color: 'var(--text-secondary)' }}>
                    Response within 24 hours
                  </li>
                  <li className="body-small" style={{ color: 'var(--text-secondary)' }}>
                    Free discovery call to understand your needs
                  </li>
                  <li className="body-small" style={{ color: 'var(--text-secondary)' }}>
                    Custom proposal tailored to your stage
                  </li>
                  <li className="body-small" style={{ color: 'var(--text-secondary)' }}>
                    No pressure, just genuine conversation
                  </li>
                </ul>
              </div>
            </div>

            {/* Contact Form */}
            <div>
              <div className="card" style={{ padding: '2.5rem' }}>
                <h2 className="heading-2" style={{ marginBottom: '1.5rem' }}>
                  Send Us a Message
                </h2>

                <form onSubmit={handleSubmit}>
                  <div style={{ marginBottom: '1.5rem' }}>
                    <label htmlFor="name" className="body-small" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                      Name *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      style={{
                        width: '100%',
                        padding: '0.75rem 1rem',
                        border: '1px solid var(--border-input)',
                        borderRadius: '0.5rem',
                        fontSize: '1rem',
                        fontFamily: 'inherit',
                        transition: 'border-color 0.2s ease',
                      }}
                      onFocus={(e) => e.target.style.borderColor = 'var(--border-input-focus)'}
                      onBlur={(e) => e.target.style.borderColor = 'var(--border-input)'}
                    />
                  </div>

                  <div style={{ marginBottom: '1.5rem' }}>
                    <label htmlFor="email" className="body-small" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                      Email *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      style={{
                        width: '100%',
                        padding: '0.75rem 1rem',
                        border: '1px solid var(--border-input)',
                        borderRadius: '0.5rem',
                        fontSize: '1rem',
                        fontFamily: 'inherit',
                        transition: 'border-color 0.2s ease',
                      }}
                      onFocus={(e) => e.target.style.borderColor = 'var(--border-input-focus)'}
                      onBlur={(e) => e.target.style.borderColor = 'var(--border-input)'}
                    />
                  </div>

                  <div style={{ marginBottom: '1.5rem' }}>
                    <label htmlFor="phone" className="body-small" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                      Phone (optional)
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      style={{
                        width: '100%',
                        padding: '0.75rem 1rem',
                        border: '1px solid var(--border-input)',
                        borderRadius: '0.5rem',
                        fontSize: '1rem',
                        fontFamily: 'inherit',
                        transition: 'border-color 0.2s ease',
                      }}
                      onFocus={(e) => e.target.style.borderColor = 'var(--border-input-focus)'}
                      onBlur={(e) => e.target.style.borderColor = 'var(--border-input)'}
                    />
                  </div>

                  <div style={{ marginBottom: '1.5rem' }}>
                    <label htmlFor="serviceInterest" className="body-small" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                      Service of Interest *
                    </label>
                    <select
                      id="serviceInterest"
                      name="serviceInterest"
                      value={formData.serviceInterest}
                      onChange={handleChange}
                      required
                      style={{
                        width: '100%',
                        padding: '0.75rem 1rem',
                        border: '1px solid var(--border-input)',
                        borderRadius: '0.5rem',
                        fontSize: '1rem',
                        fontFamily: 'inherit',
                        transition: 'border-color 0.2s ease',
                        backgroundColor: 'white',
                      }}
                      onFocus={(e) => e.target.style.borderColor = 'var(--border-input-focus)'}
                      onBlur={(e) => e.target.style.borderColor = 'var(--border-input)'}
                    >
                      <option value="">Select a service...</option>
                      <option value="discover">DISCOVER Package</option>
                      <option value="launch">LAUNCH Package</option>
                      <option value="foundations">FOUNDATIONS Package</option>
                      <option value="grow">GROW Package</option>
                      <option value="alacarte">À La Carte Services</option>
                      <option value="membership">Entrepreneur Wellness Club</option>
                      <option value="general">General Inquiry</option>
                    </select>
                  </div>

                  <div style={{ marginBottom: '1.5rem' }}>
                    <label htmlFor="message" className="body-small" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                      Message *
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows="5"
                      placeholder="Tell us about your business and what you're looking for..."
                      style={{
                        width: '100%',
                        padding: '0.75rem 1rem',
                        border: '1px solid var(--border-input)',
                        borderRadius: '0.5rem',
                        fontSize: '1rem',
                        fontFamily: 'inherit',
                        resize: 'vertical',
                        transition: 'border-color 0.2s ease',
                      }}
                      onFocus={(e) => e.target.style.borderColor = 'var(--border-input-focus)'}
                      onBlur={(e) => e.target.style.borderColor = 'var(--border-input)'}
                    />
                  </div>

                  <button type="submit" className="btn-primary" style={{ width: '100%', gap: '0.5rem' }}>
                    Send Message
                    <Send size={16} />
                  </button>

                  <p className="caption" style={{ marginTop: '1rem', textAlign: 'center', color: 'var(--text-muted)' }}>
                    We'll respond within 24 hours to schedule your discovery call
                  </p>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Additional CTA */}
      <section className="section-sm" style={{ background: 'var(--bg-section)' }}>
        <div className="container !mx-[10px]" style={{ textAlign: 'center' }}>
          <div style={{ maxWidth: '700px', margin: '0 auto' }}>
            <h2 className="heading-2" style={{ marginBottom: '1rem' }}>
              Prefer to schedule directly?
            </h2>
            <p className="body-medium" style={{ marginBottom: '1.5rem', color: 'var(--text-secondary)' }}>
              Book a free 30-minute discovery call at a time that works for you
            </p>
            <a 
              href={contactInfo.calendly}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-secondary"
              style={{ display: 'inline-block', textDecoration: 'none' }}
            >
              Schedule Discovery Call
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
